import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';

const CONSENT_KEY = 'revealiq_cookie_consent';

/**
 * Cookie / data-processing consent banner (DPDP + GDPR).
 * Shown until the user accepts or declines; choice persisted in localStorage.
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(CONSENT_KEY)) {
        setVisible(true);
      }
    } catch (e) {
      // localStorage unavailable — show the banner anyway.
      setVisible(true);
    }
  }, []);

  const record = (value) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ value, at: new Date().toISOString() }));
    } catch (e) {
      // Ignore storage failures.
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 inset-x-0 z-[60] p-4"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="max-w-3xl mx-auto glass-card p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-5 h-5 text-neon-cyan flex-shrink-0 mt-0.5" />
              <p className="text-sm text-grey-300 font-body">
                We use essential cookies and process meeting data to run RevealIQ Meet. By continuing you
                consent to this as described in our{' '}
                <Link to="/privacy" className="text-neon-cyan hover:underline">Privacy Policy</Link>.
              </p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <button
                onClick={() => record('declined')}
                className="flex-1 md:flex-none px-4 py-2 rounded-lg text-sm text-grey-300 border border-white/10 hover:bg-white/5 transition-colors"
              >
                Decline
              </button>
              <button
                onClick={() => record('accepted')}
                data-testid="cookie-accept"
                className="flex-1 md:flex-none px-5 py-2 rounded-lg text-sm font-semibold bg-neon-cyan text-black hover:bg-neon-cyan/80 transition-colors"
              >
                Accept
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
